import type { WritableComputedRef } from 'vue';
import { computed } from 'vue';

import { useClientStore } from '@/features/Clients/store';
import type { ClientResource } from '@/features/Clients/types';
import { ROLE } from '@/features/Clients/types';
import { useRefCounter } from '@/features/EntityManager/useRefCounter';

interface UseWritableClients {
  vnProxy: WritableComputedRef<ClientResource | undefined>;
  vpProxy: WritableComputedRef<ClientResource | undefined>;
  zpProxy: WritableComputedRef<ClientResource | undefined>;
  mvpProxy: WritableComputedRef<ClientResource | undefined>;
  addNewClient: (roles: ROLE[]) => number;
  createRefIdProxy: (
    refId: number,
  ) => WritableComputedRef<ClientResource | undefined>;
}

export const useWritableClients = (): UseWritableClients => {
  const clientStore = useClientStore();

  const vnProxy = computed({
    get() {
      return clientStore[ROLE.VERSICHERUNGSNEHMER].data;
    },
    set(value: ClientResource | undefined) {
      if (value) {
        clientStore.setClientDataByRole(ROLE.VERSICHERUNGSNEHMER, value);
      }
    },
  });

  const vpProxy = computed({
    get() {
      return clientStore[ROLE.VERSICHERTE_PERSON].data;
    },
    set(value: ClientResource | undefined) {
      if (value) {
        clientStore.setClientDataByRole(ROLE.VERSICHERTE_PERSON, value);
      }
    },
  });

  const zpProxy = computed({
    get() {
      return clientStore[ROLE.ZAHLPERSON].data;
    },
    set(value: ClientResource | undefined) {
      if (value) {
        clientStore.setClientDataByRole(ROLE.ZAHLPERSON, value);
      }
    },
  });

  const mvpProxy = computed({
    get() {
      return clientStore[ROLE.MITVERSICHERTER_PARTNER].data;
    },
    set(value: ClientResource | undefined) {
      if (value) {
        clientStore.setClientDataByRole(ROLE.MITVERSICHERTER_PARTNER, value);
      }
    },
  });

  const addNewClient = (roles: ROLE[]): number => {
    clientStore.addNewClient(roles);
    return useRefCounter().refCounter.value;
  };

  const createRefIdProxy = (
    refId: number,
  ): WritableComputedRef<ClientResource | undefined> =>
    computed({
      get() {
        return clientStore.getClientByRefId(refId).data;
      },
      set(value: ClientResource | undefined) {
        if (value) {
          clientStore.setClientDataByRefId(refId, value);
        }
      },
    });

  return {
    vnProxy,
    vpProxy,
    zpProxy,
    mvpProxy,
    addNewClient,
    createRefIdProxy,
  };
};
